import { useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatError } from "@/lib/api";
import { Sparkles, Upload, X, Check, Image as ImageIcon } from "lucide-react";

const emptyForm = {
  name: "", email: "", phone: "", grad_year: "",
  current_role: "", location: "", story: "",
};

export default function Alumni() {
  const { t } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const photoRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Max 15 MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto({ url: res.data.url, filename: res.data.filename || file.name });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) return toast.error(t.alumni.form.err_missing_photo);
    setSending(true);
    try {
      const programLine = [form.grad_year && `Promo ${form.grad_year}`, form.current_role, form.location].filter(Boolean).join(" · ");
      await api.post("/inquiries", {
        type: "alumni",
        name: form.name,
        email: form.email,
        phone: form.phone,
        program: programLine,
        message: form.story,
        student_name: form.name,
        student_class: form.grad_year ? `Promo ${form.grad_year}` : "",
        student_photo_url: photo.url,
        parent_photo_url: "",
        document_urls: [],
      });
      toast.success(t.alumni.form.success);
      setSuccess(true);
      setForm(emptyForm);
      setPhoto(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const field = "w-full h-12 bg-transparent border-b border-icdh-white/30 focus:border-icdh-white outline-none text-icdh-white placeholder:text-icdh-white/50 transition-colors";

  return (
    <section id="alumni" data-testid="alumni-section" className="relative bg-icdh-red text-icdh-white py-24 md:py-32 overflow-hidden">
      <div className="noise-overlay absolute inset-0 opacity-[0.06] pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <Reveal><Overline dark>{t.alumni.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-2xl">{t.alumni.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-icdh-white/85 text-lg max-w-xl">{t.alumni.lead}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 inline-flex items-center gap-3 text-icdh-white/90">
              <Sparkles size={16} />
              <span className="text-xs uppercase tracking-[0.2em]">{t.alumni.cta}</span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="alumni-form" className="bg-icdh-navy/95 p-7 md:p-10 space-y-5 border border-icdh-white/10">
            <input required data-testid="alu-name" className={field} placeholder={t.alumni.form.name} value={form.name} onChange={set("name")} />
            <input required type="email" data-testid="alu-email" className={field} placeholder={t.alumni.form.email} value={form.email} onChange={set("email")} />
            <input data-testid="alu-phone" className={field} placeholder={t.alumni.form.phone} value={form.phone} onChange={set("phone")} />
            <div className="grid sm:grid-cols-2 gap-5">
              <input required data-testid="alu-year" className={field} type="number" min="1990" max="2030" placeholder={t.alumni.form.grad_year} value={form.grad_year} onChange={set("grad_year")} />
              <input data-testid="alu-location" className={field} placeholder={t.alumni.form.location} value={form.location} onChange={set("location")} />
            </div>
            <input data-testid="alu-role" className={field} placeholder={t.alumni.form.current_role} value={form.current_role} onChange={set("current_role")} />

            <div data-testid="alu-photo-slot" className="border border-dashed border-icdh-white/30 p-4 flex items-center gap-4">
              <div className="h-11 w-11 bg-icdh-white/10 flex items-center justify-center flex-shrink-0">
                <ImageIcon size={20} className="text-icdh-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{t.alumni.form.photo}</p>
                {photo
                  ? <p className="mt-0.5 text-xs text-icdh-white/70 truncate" data-testid="alu-photo-filename">{photo.filename}</p>
                  : <p className="mt-0.5 text-xs text-icdh-white/60">{t.alumni.form.photo_hint}</p>}
              </div>
              {photo ? (
                <button type="button" onClick={() => setPhoto(null)} data-testid="alu-photo-clear" className="h-9 w-9 border border-icdh-white/30 hover:bg-icdh-white hover:text-icdh-red flex items-center justify-center transition-colors">
                  <X size={14} />
                </button>
              ) : (
                <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading} data-testid="alu-photo-pick"
                  className="h-9 px-3 bg-icdh-white text-icdh-red text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 whitespace-nowrap hover:bg-icdh-navy hover:text-icdh-white transition-colors disabled:opacity-60">
                  <Upload size={14} />{uploading ? "..." : t.alumni.form.photo}
                </button>
              )}
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="alu-photo-input" />
            </div>

            <textarea required data-testid="alu-story" rows={4} className={`${field} h-auto resize-none pt-3`} placeholder={t.alumni.form.story_placeholder} value={form.story} onChange={set("story")} />

            <button type="submit" disabled={sending} data-testid="alu-submit"
              className="w-full h-14 bg-icdh-white text-icdh-red text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy hover:text-icdh-white transition-colors duration-300 disabled:opacity-60">
              {sending ? t.alumni.form.sending : t.alumni.form.submit}
            </button>
          </form>
        </Reveal>
      </div>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-icdh-navy/80 backdrop-blur-sm flex items-center justify-center p-6"
            data-testid="alu-success-modal"
            onClick={() => setSuccess(false)}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
              className="relative bg-icdh-white max-w-md w-full p-8 md:p-12 text-icdh-navy"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-14 w-14 bg-icdh-red flex items-center justify-center">
                <Check className="text-icdh-white" size={28} />
              </div>
              <h3 className="mt-6 font-serif text-3xl tracking-tight">{t.alumni.cta}</h3>
              <p className="mt-4 text-icdh-navy/75 leading-relaxed">{t.alumni.form.success}</p>
              <button onClick={() => setSuccess(false)} data-testid="alu-success-close"
                className="mt-8 h-12 px-6 bg-icdh-navy text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-red transition-colors">
                OK
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
